import React from 'react';
import BackgroundShapes from '../common/BackgroundShapes'; // Default import

interface LandingPageProps {
  onNavigate: (page: string) => void;
}

const LandingPage: React.FC<LandingPageProps> = ({ onNavigate }) => {
  // Structured Data (JSON-LD) for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "ShiYou",
    "description": "AI-powered roommate matching platform for students and young professionals in NYC and Jersey City",
    "url": "https://shiyou.app",
    "applicationCategory": "LifestyleApplication",
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    }
  };

  return (
    <>
      {/* Structured Data for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      
      <div className="min-h-screen bg-paper relative">
      <BackgroundShapes />
      
      <div className="container mx-auto px-4 py-12 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-8">
            <h1 className="text-6xl font-bold text-coffee mb-4">
              ShiYou: Live Better Together
            </h1>
            <p className="text-coffee text-2xl mb-12">NYC & Jersey City's smartest way to find roommates and shared homes</p>
            
            {/* Primary CTA */}
            <div className="flex flex-col md:flex-row gap-6 justify-center items-center mb-8">
              <a
                href="/how-it-works"
                className="bg-white text-coffee px-10 py-4 rounded-xl font-bold text-xl hover:bg-cream transition-all duration-300 border-2 border-coffee hover:scale-105 transform shadow-[4px_4px_0px_#594A42]"
              >
                How ShiYou Works →
              </a>
              <button
                onClick={() => onNavigate('signup')}
                className="bg-terracotta text-white px-10 py-4 rounded-xl font-bold text-xl hover:bg-[#d89050] transition-all duration-300 border-2 border-coffee hover:scale-105 transform shadow-[4px_4px_0px_#594A42]"
              >
                → Join
              </button>
            </div>

            {/* Secondary CTA for existing users */}
            <div className="flex justify-center items-center mb-4">
              <button
                onClick={() => onNavigate('login')}
                className="text-coffee hover:text-terracotta font-medium text-lg transition-colors underline decoration-terracotta hover:decoration-terracotta"
              >
                Already have an account? Sign in
              </button>
            </div>


          </div>

          {/* Discovery Blocks */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <a
              href="/condos"
              className="group bg-cream border-2 border-coffee rounded-xl p-8 shadow-[4px_4px_0px_#594A42] hover:shadow-[6px_6px_0px_#594A42] transition-all duration-200 cursor-pointer block"
            >
              <div className="text-4xl mb-4">🏢</div>
              <h2 className="text-2xl font-bold text-coffee mb-3">Group Up</h2>
              <p className="text-coffee opacity-80 mb-6">One listing, one group. Share the link — interested people join automatically.</p>
              <span className="inline-block bg-terracotta text-white px-5 py-2 rounded-lg font-semibold border-2 border-coffee group-hover:bg-[#d89050] transition-colors">
                Browse Listings →
              </span>
            </a>

            <a
              href="/discover"
              className="group bg-cream border-2 border-coffee rounded-xl p-8 shadow-[4px_4px_0px_#594A42] hover:shadow-[6px_6px_0px_#594A42] transition-all duration-200 cursor-pointer block"
            >
              <div className="text-4xl mb-4">👥</div>
              <h2 className="text-2xl font-bold text-coffee mb-3">Find Your Best Match</h2>
              <p className="text-coffee opacity-80 mb-6">Filter by lifestyle and budget. AI ranks who fits — so you know before you reach out.</p>
              <span className="inline-block bg-terracotta text-white px-5 py-2 rounded-lg font-semibold border-2 border-coffee group-hover:bg-[#d89050] transition-colors">
                Meet Roommates →
              </span>
            </a>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default LandingPage;